import { styled, ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { GoogleOAuthProvider } from '@react-oauth/google';
import * as Sentry from '@sentry/react';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { hoursToMilliseconds } from 'date-fns';
import { MaterialDesignContent, SnackbarProvider } from 'notistack';
import { BrowserRouter } from 'react-router-dom';
import validate from 'validate.js';
import { APP_ENVIRONMENT, SENTRY_DSN, SENTRY_ENVIRONMENTS } from 'common/config/app';
import { CookieConsentProvider, ThemeModeProvider, ZoomProvider } from 'common/providers';
import reducers from 'common/reducers';
import appInitialStates from 'common/states';
import { StoreProvider } from 'common/store';
import { DefaultMutationResponse } from 'common/types/services';
import validators from 'common/validators';
import { ErrorBoundary, SnackCloseIcon } from 'components';
import DialogWrapper from 'components/DialogWrapper';
import ShortcutHelper from 'components/ShortcutHelper/ShortcutHelper';
import { UnsavedChangesWrapper } from 'components/UnsavedChanges';
import { EventBusProvider, TitleProvider, TitleUpdater } from 'context';
import { Provider as DashboardProvider } from 'dashboard409a/states/provider';
import { FeaturesProvider, FeedbackProvider } from 'providers';
import Routes from 'Routes';
import AnalyticsProvider from 'scalarAnalytics/AnalyticsProvider';
import theme from 'theme';
import { displayErrorMessage, displaySuccessMessage, getObjectValue, getStringValue } from 'utilities';
import 'react-perfect-scrollbar/dist/css/styles.css';
import 'assets/scss/index.scss';
import { AsyncProcessorProvider } from './context/AsyncProcessorContext/AsyncProcessorContext';

const PREFIX = 'App';

const classes = {
  anchorOriginTopRight: `${PREFIX}-anchorOriginTopRight`,
};

const StyledGoogleOAuthProvider = styled(GoogleOAuthProvider)({
  [`& .${classes.anchorOriginTopRight}`]: {
    top: 155,
  },
});

// Add custom validators
validate.validators = {
  ...validate.validators,
  ...validators,
};

// Sentry
Sentry.init({
  environment: APP_ENVIRONMENT,
  dsn: SENTRY_DSN,
  integrations: [
    // Simplified integration for now
  ],
  tracePropagationTargets: [window.location.hostname, getStringValue(process.env.REACT_APP_API_ENDPOINT)],
  beforeSend(event) {
    // Only track errors from staging and production
    if (!SENTRY_ENVIRONMENTS.includes(APP_ENVIRONMENT || '')) {
      return null;
    }
    return event;
  },
  tracesSampleRate: 1.0,
  replaysOnErrorSampleRate: 1.0, // Record all the sampling sessions where errors occur
});

// Notistack Components override
const StyledMaterialDesignContent = styled(MaterialDesignContent)(() => ({
  '&.notistack-MuiContent-success': {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
  },
  '&.notistack-MuiContent-error': {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
  '&.notistack-MuiContent-warning': {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
  },
  '&.notistack-MuiContent-info': {
    backgroundColor: theme.palette.info.main,
    color: theme.palette.info.contrastText,
  },
}));

// Set up react-query client with cache time
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache for 24 hours
      cacheTime: hoursToMilliseconds(24),
      // Display error message
      onError: error => displayErrorMessage(error instanceof Error ? error.message : (error as string)),
      // Do not retry on error
      retry: false,
      // The query will not be retried on mount if it contains an error
      retryOnMount: false,
    },
    mutations: {
      // Display error message
      onError: error => displayErrorMessage(error instanceof Error ? error.message : (error as string)),
      // Display success message
      onSuccess: data => {
        const { responseMessage } = getObjectValue(data) as DefaultMutationResponse;

        if (responseMessage) {
          displaySuccessMessage(responseMessage);
        }
      },
    },
  },
});

// Set up react-query local storage persister
const persister = createSyncStoragePersister({
  storage: window.localStorage,
});

const App = () => (
  <StyledGoogleOAuthProvider clientId={`${process.env.REACT_APP_GOOGLE_CLIENT_ID}`}>
    <AsyncProcessorProvider>
      <StoreProvider initialState={appInitialStates} reducer={reducers}>
        <PersistQueryClientProvider client={queryClient} persistOptions={{ persister }}>
          <DashboardProvider>
            <EventBusProvider>
              <ThemeModeProvider>
                <ThemeProvider theme={theme}>
                  <ZoomProvider>
                    <BrowserRouter>
                      <CookieConsentProvider>
                        <TitleProvider>
                          <TitleUpdater />
                          <AnalyticsProvider>
                            <FeedbackProvider>
                              <ErrorBoundary>
                                <DialogWrapper>
                                  <UnsavedChangesWrapper>
                                    <SnackbarProvider
                                      action={SnackCloseIcon}
                                      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                      autoHideDuration={3000}
                                      Components={{ success: StyledMaterialDesignContent }}
                                      maxSnack={3}
                                      preventDuplicate
                                      SnackbarProps={{
                                        style: {
                                          top: '155px',
                                        },
                                      }}>
                                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <FeaturesProvider>
                                          <ShortcutHelper>
                                            <Routes />
                                          </ShortcutHelper>
                                        </FeaturesProvider>
                                      </LocalizationProvider>
                                    </SnackbarProvider>
                                  </UnsavedChangesWrapper>
                                </DialogWrapper>
                              </ErrorBoundary>
                            </FeedbackProvider>
                          </AnalyticsProvider>
                        </TitleProvider>
                      </CookieConsentProvider>
                    </BrowserRouter>
                  </ZoomProvider>
                </ThemeProvider>
              </ThemeModeProvider>
            </EventBusProvider>
          </DashboardProvider>
        </PersistQueryClientProvider>
      </StoreProvider>
    </AsyncProcessorProvider>
  </StyledGoogleOAuthProvider>
);

export default Sentry.withProfiler(App);
